/**
 * Messages context — the hotel-wide unread/needs-reply badge for the
 * Messages tab (guest inbox: email + OTA chat). Modelled on
 * GuestRequestsContext.js.
 *
 * Keeps `summary` fresh three ways:
 *  1. 60s polling (always-on fallback, paused while the tab is hidden)
 *  2. WebSocket guest_message_event pushes → debounced refetch
 *  3. refreshSummary() after a staff action inside the Messages page
 *
 * A new inbound message ('message.received') raises a toast + chime so a
 * guest reply doesn't sit unseen while staff are on another tab. The
 * Messages page itself (not this context) handles refetching the open
 * conversation/list — this context only owns the badge.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import messagingService from '../service/MessagingService';
import webSocketService from '../service/WebSocketService';

const POLL_INTERVAL_MS = 60 * 1000;
const WS_REFRESH_DEBOUNCE_MS = 800;

const MessagesContext = createContext(null);

export const useMessages = () => useContext(MessagesContext);

/** Same two-tone WebAudio chime as GuestRequestsContext — no asset file. */
const playChime = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    [[880, 0], [1174.66, 0.18]].forEach(([freq, delay]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delay + 0.35);
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 0.4);
    });
    setTimeout(() => ctx.close(), 1000);
  } catch (e) {
    // Audio is best-effort (autoplay policies etc.)
  }
};

export const MessagesProvider = ({ children }) => {
  const [summary, setSummary] = useState({ unreadTotal: 0, awaitingReply: 0, enabled: true });
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const { t } = useTranslation();
  const wsRefreshTimer = useRef(null);

  const refreshSummary = useCallback(async () => {
    try {
      const data = await messagingService.getSummary();
      if (!data || typeof data.unreadTotal !== 'number') {
        console.warn('Messages: unexpected summary response shape, got:', data);
        return;
      }
      setSummary({
        unreadTotal: data.unreadTotal || 0,
        awaitingReply: data.awaitingReply || 0,
        enabled: data.enabled !== false,
      });
    } catch (error) {
      console.warn('Messages summary refresh failed:', error?.message || error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll as the always-on fallback — only while the tab is visible.
  useEffect(() => {
    refreshSummary();
    const interval = setInterval(() => {
      if (!document.hidden) refreshSummary();
    }, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (!document.hidden) refreshSummary();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshSummary]);

  // Real-time: refetch the badge (debounced) on every push; chime + toast
  // on a brand-new inbound guest message.
  useEffect(() => {
    const handleEvent = (data) => {
      if (wsRefreshTimer.current) clearTimeout(wsRefreshTimer.current);
      wsRefreshTimer.current = setTimeout(refreshSummary, WS_REFRESH_DEBOUNCE_MS);

      if (data?.event === 'message.received') {
        toast({
          title: t('ui:messages.toasts.newMessageTitle', { guestName: data.guestName || t('ui:messages.toasts.unknownGuest') }),
          description: data.preview || '',
          status: 'info',
          duration: 8000,
          isClosable: true,
          position: 'top-right',
        });
        playChime();
      }
    };

    webSocketService.on('guest_message_event', handleEvent);
    return () => {
      webSocketService.off('guest_message_event', handleEvent);
      if (wsRefreshTimer.current) clearTimeout(wsRefreshTimer.current);
    };
  }, [refreshSummary, toast, t]);

  const value = { summary, loading, refreshSummary };

  return (
    <MessagesContext.Provider value={value}>
      {children}
    </MessagesContext.Provider>
  );
};

export default MessagesContext;
